import { ArrowLeftOutlined, MailOutlined } from "@ant-design/icons";
import { Alert, Form, Input, Radio } from "antd";
import { configs } from "configs";
import { getAppLanguage } from "helpers/getAppLanguage";
import {
  Button,
  InputOtpField,
  Modal,
  notification,
  PhoneInput,
} from "components/common";
import { LogoPropone } from "components/svg";
import { LOGIN_STORAGE_KEY, USER_STORAGE_KEY } from "constants";
import {
  useDeveloperForgotPinMutation,
  useDeveloperPinMutation,
  useDeveloperPinStatusMutation,
  useDeveloperSetPinMutation,
  useDeveloperSignUpMutation,
  useDeveloperVerifyOtpMutation,
} from "features/auth/api";
import { getDeveloperDashboardPath } from "helpers/getDeveloperDashboardPath";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiPhone } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToken, setAppUser, setIsLoggedIn } from "store/appSlice";
import { getLoginErrorMessage, updateAuthCredentials } from "utils/authHelpers";
import { setStorageItem } from "utils/localStorage";
import styles from "./developerLogin.module.less";

const STEPS = {
  WELCOME: "welcome",
  OTP: "otp",
  CREATE_PIN: "createPin",
  CONFIRM_PIN: "confirmPin",
  ENTER_PIN: "enterPin",
};

const OTP_FLOW = {
  SIGNUP: "signup",
  FORGOT: "forgot",
};

const pickHasPin = (data) => {
  if (data == null || typeof data !== "object") return false;
  const v = data.pin_status ?? data.is_pin_set;
  if (typeof v === "boolean") return v;
  if (v === true || v === 1 || v === "1" || v === "true") return true;
  return false;
};

/** Display name from `developer_pin_status` when user already has a PIN (several possible shapes). */
const pickNameFromPinStatus = (data) => {
  if (data == null || typeof data !== "object") return "";
  return data.name ?? "";
};

const maskContact = (id) => {
  if (!id) return "";
  if (id.includes("@")) {
    const [local, domain] = id.split("@");
    if (!local || !domain) return id;
    if (local.length <= 2) return `**@${domain}`;
    return `${local[0]}***${local[local.length - 1]}@${domain}`;
  }
  const tail = id.slice(-4);
  return `***${tail}`;
};

/** Last digits for “WhatsApp ending in …” (E.164 or local digits). */
const maskPhoneEnding = (phone) => {
  if (!phone) return "";
  const s = String(phone).trim();
  if (s.includes("@")) return maskContact(s);
  const digits = s.replace(/\D/g, "");
  if (digits.length >= 4) return `***${digits.slice(-4)}`;
  if (digits.length > 0) return `***${digits}`;
  return maskContact(s);
};

/** Maps identifier string to API body: `email` or `primary_contact` (phone / E.164). */
const identifierApiBody = (id) => {
  const trimmed = (id || "").trim();
  if (!trimmed) return {};
  return trimmed.includes("@")
    ? { email: trimmed }
    : { primary_contact: trimmed };
};

const DeveloperLogin = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const direction = useSelector((s) => s.app.configs.direction);
  const currentLang = getAppLanguage()?.key;

  /** Switch UI language by swapping the lang prefix and reloading so i18n,
   *  direction and fonts re-initialise for the new locale. */
  const onSwitchLanguage = (key) => {
    if (!key || key === currentLang) return;
    const pathWithoutLang = window.location.pathname.replace(/^\/(en|ar)/, "");
    window.location.href = `/${key}${pathWithoutLang}${window.location.search}`;
  };

  const [step, setStep] = useState(STEPS.WELCOME);
  /** false = phone field (default); true = email field — toggled via “Continue with Email / Phone”. */
  const [showEmailField, setShowEmailField] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [phoneValue, setPhoneValue] = useState("");
  const [primaryContact, setPrimaryContact] = useState("");
  const [otpFlow, setOtpFlow] = useState(OTP_FLOW.SIGNUP);
  const [otp, setOtp] = useState("");
  const [createPin, setCreatePin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [enterPin, setEnterPin] = useState("");
  const [draftPin, setDraftPin] = useState("");
  const [pinMismatch, setPinMismatch] = useState(false);
  const [pinAuthError, setPinAuthError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resendSec, setResendSec] = useState(0);
  /** Set when pin_status returns has_pin — used on Enter PIN step (“Welcome back, …”). */
  const [welcomeBackName, setWelcomeBackName] = useState("");
  /** From `developer_pin_status` `data` — used for forgot PIN + OTP copy (`email`, `primary_contact`). */
  const [pinMeta, setPinMeta] = useState({ email: "", primary_contact: "" });

  const [pinStatus, { isLoading: pinStatusLoading }] =
    useDeveloperPinStatusMutation();
  const [signUp, { isLoading: signUpLoading }] = useDeveloperSignUpMutation();
  const [verifyOtp, { isLoading: verifyLoading }] =
    useDeveloperVerifyOtpMutation();
  const [setPinApi, { isLoading: setPinLoading }] =
    useDeveloperSetPinMutation();
  const [pinLogin, { isLoading: pinLoginLoading }] = useDeveloperPinMutation();
  const [forgotPinApi, { isLoading: forgotLoading }] =
    useDeveloperForgotPinMutation();

  const loading =
    pinStatusLoading ||
    signUpLoading ||
    verifyLoading ||
    setPinLoading ||
    pinLoginLoading ||
    forgotLoading;

  /** Hidden master-login entry: 5 clicks on the welcome heading prompts for
   *  the impersonated user's email, then opens the impersonation page. The
   *  uuid is cosmetic - authorization is backend-side. */
  const impersonateClicks = useRef(0);
  const impersonateModalRef = useRef(null);
  const [impersonateForm] = Form.useForm();
  const onWelcomeHeadingClick = () => {
    impersonateClicks.current += 1;
    if (impersonateClicks.current < 5) return;
    impersonateClicks.current = 0;
    impersonateModalRef.current?.setVisible(true);
  };
  const onImpersonateEmailOk = async () => {
    let values;
    try {
      values = await impersonateForm.validateFields();
    } catch {
      return;
    }
    const uuid = window.crypto?.randomUUID?.() || `${Date.now()}`;
    navigate(`/${uuid}/impersonate`, {
      state: { targetEmail: values.email.trim() },
    });
  };

  const resolvePrimaryContact = useCallback(() => {
    if (showEmailField) return emailValue.trim();
    return phoneValue || "";
  }, [showEmailField, emailValue, phoneValue]);

  const handleAuthSuccess = (response) => {
    const authHeaders = response?.authHeaders;
    const user = response?.user ?? response?.data?.user;
    if (user && authHeaders?.["access-token"]) {
      updateAuthCredentials({ data: { user } }, authHeaders);
      dispatch(setAppToken({ token: authHeaders["access-token"] }));
      dispatch(setAppUser({ info: user }));
      setStorageItem(USER_STORAGE_KEY, user);
      setStorageItem(LOGIN_STORAGE_KEY, "yes");
      dispatch(setIsLoggedIn(true));
      navigate(getDeveloperDashboardPath());
    } else {
      setErrorMessage(t("Login failed. Please try again."));
    }
  };

  const onWelcomeContinue = async () => {
    setErrorMessage("");
    const pc = resolvePrimaryContact();
    if (!pc) {
      setErrorMessage(
        showEmailField
          ? t("Please enter your email")
          : t("Please enter your phone number"),
      );
      return;
    }
    try {
      const raw = await pinStatus(identifierApiBody(pc)).unwrap();
      const data = raw?.data ?? raw;
      const hasPin = pickHasPin(data);
      setPrimaryContact(pc);
      setPinMeta({
        email: (data?.email ?? "").toString().trim(),
        primary_contact: (data?.primary_contact ?? "").toString().trim(),
      });
      if (hasPin) {
        setWelcomeBackName(pickNameFromPinStatus(data));
        setStep(STEPS.ENTER_PIN);
        setEnterPin("");
        setPinAuthError(false);
      } else {
        await signUp(identifierApiBody(pc)).unwrap();
        setOtpFlow(OTP_FLOW.SIGNUP);
        setStep(STEPS.OTP);
        setOtp("");
        setResendSec(30);
        notification.success({
          message: t("Verification code sent"),
          description: t("Enter the verification code we sent to continue."),
        });
      }
    } catch (err) {
      setErrorMessage(getLoginErrorMessage(err));
    }
  };

  const onVerifyOtp = async () => {
    setErrorMessage("");
    if (otp.length !== 4) return;
    try {
      await verifyOtp({ ...identifierApiBody(primaryContact), otp }).unwrap();
      notification.success({
        message: t("Verification completed"),
        description: t("Use your new PIN to sign in next time."),
      });
      setStep(STEPS.CREATE_PIN);
      setCreatePin("");
      setConfirmPin("");
      setDraftPin("");
      setPinMismatch(false);
    } catch (err) {
      setErrorMessage(t("Invalid OTP"));
    }
  };

  const onContinueCreatePin = () => {
    setErrorMessage("");
    if (createPin.length !== 4) return;
    setDraftPin(createPin);
    setStep(STEPS.CONFIRM_PIN);
    setConfirmPin("");
    setPinMismatch(false);
  };

  const onConfirmPinSubmit = async () => {
    setErrorMessage("");
    if (confirmPin.length !== 4) return;
    if (confirmPin !== draftPin) {
      setPinMismatch(true);
      setConfirmPin("");
      return;
    }
    try {
      const res = await setPinApi({
        ...identifierApiBody(primaryContact),
        pin: confirmPin,
        otp: otp,
      }).unwrap();
      notification.success({
        message: t("PIN created successfully"),
        description: t("Use it to sign in next time."),
      });
      handleAuthSuccess(res);
    } catch (err) {
      setErrorMessage(getLoginErrorMessage(err));
    }
  };

  const onPinLogin = async () => {
    setErrorMessage("");
    if (enterPin.length !== 4) return;
    try {
      const res = await pinLogin({
        ...identifierApiBody(primaryContact),
        pin: enterPin,
      }).unwrap();
      handleAuthSuccess(res);
    } catch (err) {
      setPinAuthError(true);
      setEnterPin("");
      setErrorMessage(t("Incorrect PIN. Please try again."));
    }
  };

  const forgotPinRequestBody = () => ({
    email: pinMeta.email ?? "",
    primary_contact: pinMeta.primary_contact ?? "",
  });

  const startForgotPinFlow = async () => {
    setErrorMessage("");
    try {
      await forgotPinApi(forgotPinRequestBody()).unwrap();
      setOtpFlow(OTP_FLOW.FORGOT);
      setStep(STEPS.OTP);
      setOtp("");
      setResendSec(30);
      notification.success({
        message: t("Verification code sent!"),
        description: t(
          "Enter the verification code we sent to reset your PIN.",
        ),
      });
    } catch (err) {
      setErrorMessage(getLoginErrorMessage(err));
    }
  };

  const onResend = async () => {
    if (resendSec > 0) return;
    setErrorMessage("");
    try {
      if (otpFlow === OTP_FLOW.SIGNUP) {
        await signUp(identifierApiBody(primaryContact)).unwrap();
      } else {
        await forgotPinApi(forgotPinRequestBody()).unwrap();
      }
      setResendSec(30);
      notification.success({
        message: t("Verification code sent!"),
        description: t("A new verification code has been sent."),
      });
    } catch (err) {
      setErrorMessage(getLoginErrorMessage(err));
    }
  };

  useEffect(() => {
    if (step !== STEPS.OTP || resendSec <= 0) return undefined;
    const id = setTimeout(() => setResendSec((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(id);
  }, [step, resendSec]);

  const resetToWelcome = () => {
    setStep(STEPS.WELCOME);
    setOtp("");
    setCreatePin("");
    setConfirmPin("");
    setEnterPin("");
    setWelcomeBackName("");
    setPinMeta({ email: "", primary_contact: "" });
    setErrorMessage("");
    setPinMismatch(false);
    setPinAuthError(false);
  };

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue.trim());
  const welcomeDisabled =
    loading || (showEmailField ? !emailValid : !phoneValue?.trim());
  const otpDisabled = loading || otp.length !== 4;
  const createPinContinueDisabled = createPin.length !== 4;
  const confirmDisabled = loading || confirmPin.length !== 4;
  const enterDisabled = loading || enterPin.length !== 4;

  const otpPhoneMasked = maskPhoneEnding(pinMeta.primary_contact);
  const otpEmailMasked = pinMeta.email ? maskContact(pinMeta.email) : "";
  const topErrorAlert = errorMessage ? (
    <Alert
      message={errorMessage}
      type="error"
      className={styles.errorAlertTop}
      onClose={() => setErrorMessage("")}
    />
  ) : null;

  return (
    <div
      className={`${styles.splitPage} ${direction === "rtl" ? styles.rtl : ""}`}
    >
      {configs.languages?.length > 1 && (
        <Radio.Group
          className={styles.languageSwitcher}
          buttonStyle="solid"
          size="small"
          value={currentLang}
          onChange={(e) => onSwitchLanguage(e.target.value)}
          data-testid="dev-login-lang-switch"
        >
          {configs.languages.map((lang) => (
            <Radio.Button
              key={lang.key}
              value={lang.key}
              data-testid={`dev-login-lang-option-${lang.key}`}
            >
              {lang.label}
            </Radio.Button>
          ))}
        </Radio.Group>
      )}
      <div className={styles.leftPanel}>
        <div className={styles.loginHeader}>
          <LogoPropone width={180} height="auto" className={styles.logo} />
        </div>
        <div className={styles.leftInner}>
          <div className={styles.loginCardWrap}>
            <div className={styles.loginCard}>
              {step === STEPS.OTP && (
                <button
                  type="button"
                  className={`${styles.backButton} ${resendSec > 0 || loading ? styles.backButtonDisabled : ""}`}
                  disabled={resendSec > 0 || loading}
                  aria-label={t("Back")}
                  onClick={() => {
                    if (resendSec > 0 || loading) return;
                    setOtp("");
                    setResendSec(0);
                    if (otpFlow === OTP_FLOW.FORGOT) {
                      setStep(STEPS.ENTER_PIN);
                    } else {
                      resetToWelcome();
                    }
                  }}
                >
                  <ArrowLeftOutlined className={styles.backButtonIcon} />
                </button>
              )}
              {step === STEPS.CONFIRM_PIN && (
                <button
                  type="button"
                  className={styles.backButton}
                  onClick={() => {
                    setStep(STEPS.CREATE_PIN);
                    setConfirmPin("");
                    setPinMismatch(false);
                    setErrorMessage("");
                  }}
                >
                  <ArrowLeftOutlined className={styles.backButtonIcon} />
                </button>
              )}
              {step === STEPS.ENTER_PIN && (
                <button
                  type="button"
                  className={styles.backButton}
                  onClick={resetToWelcome}
                  aria-label={t("Back")}
                >
                  <ArrowLeftOutlined className={styles.backButtonIcon} />
                </button>
              )}
              {step === STEPS.WELCOME && (
                <form
                  className={styles.stepForm}
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (welcomeDisabled) return;
                    void onWelcomeContinue();
                  }}
                >
                  <h4
                    className={styles.welcomeHeading}
                    onClick={onWelcomeHeadingClick}
                  >
                    {t("Welcome to Propone!")}
                  </h4>
                  <p className={styles.subtitleSecondary}>
                    {t("Use your phone number or email to get started")}
                  </p>
                  {topErrorAlert}
                  <Form
                    component="div"
                    layout="vertical"
                    className={styles.form}
                  >
                    {showEmailField ? (
                      <Form.Item label={t("Email")}>
                        <Input
                          size="large"
                          className={styles.emailInput}
                          placeholder={t("name@email.com")}
                          value={emailValue}
                          onChange={(e) => setEmailValue(e.target.value)}
                          disabled={loading}
                        />
                      </Form.Item>
                    ) : (
                      <Form.Item label={t("Phone Number")}>
                        <div
                          className={styles.phoneInputWrap}
                          data-testid="dev-login-phone-input"
                        >
                          <PhoneInput
                            value={phoneValue}
                            onChange={setPhoneValue}
                            placeholder={t("Enter phone number")}
                            defaultCountry="SA"
                            disabled={loading}
                          />
                        </div>
                      </Form.Item>
                    )}
                  </Form>
                  <div className={styles.buttonGroup}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      block
                      className={styles.loginButton}
                      loading={loading}
                      disabled={welcomeDisabled}
                    >
                      {t("Continue")}
                    </Button>
                  </div>
                  <div className={styles.orDivider}>{t("OR")}</div>
                  <button
                    type="button"
                    className={styles.modeSwitchButton}
                    onClick={() => {
                      setShowEmailField((v) => !v);
                      setErrorMessage("");
                    }}
                    disabled={loading}
                  >
                    {showEmailField ? (
                      <>
                        <FiPhone
                          className={styles.modeSwitchIcon}
                          aria-hidden
                        />
                        <span>{t("Continue with Phone Number")}</span>
                      </>
                    ) : (
                      <>
                        <MailOutlined
                          className={styles.modeSwitchIcon}
                          aria-hidden
                        />
                        <span>{t("Continue with Email")}</span>
                      </>
                    )}
                  </button>
                </form>
              )}

              {step === STEPS.OTP && (
                <form
                  className={styles.stepForm}
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (otpDisabled) return;
                    void onVerifyOtp();
                  }}
                >
                  <h4 className={styles.title}>{t("Verification")}</h4>
                  {otpPhoneMasked && otpEmailMasked ? (
                    <p className={styles.subtitle}>
                      {t(
                        "Please enter the OTP sent to your WhatsApp ending in {{phone}} and to your email {{email}}",
                        { phone: otpPhoneMasked, email: otpEmailMasked },
                      )}
                    </p>
                  ) : otpPhoneMasked && !otpEmailMasked ? (
                    <p className={styles.subtitle}>
                      {t(
                        "Please enter the OTP sent to your WhatsApp ending in {{phone}}.",
                        { phone: otpPhoneMasked },
                      )}
                    </p>
                  ) : !otpPhoneMasked && otpEmailMasked ? (
                    <p className={styles.subtitle}>
                      {t("Please enter the OTP sent to your email {{email}}.", {
                        email: otpEmailMasked,
                      })}
                    </p>
                  ) : (
                    <p className={styles.subtitle}>
                      {t("We've sent a verification code to")}{" "}
                      {maskContact(primaryContact)}
                    </p>
                  )}
                  {topErrorAlert}
                  <div className={styles.form}>
                    <p className={styles.otpStepLabel}>{t("Enter OTP")}</p>
                    <div
                      className={`${styles.otpFieldWrap} ${styles.enterPinOtpFull}`}
                    >
                      <InputOtpField
                        length={4}
                        value={otp}
                        onValueChange={setOtp}
                      />
                    </div>
                  </div>
                  <div className={styles.resendRow}>
                    {resendSec > 0 ? (
                      <span>
                        {t("Resend in")} {resendSec}s
                      </span>
                    ) : (
                      <button
                        type="button"
                        className={styles.resendLink}
                        onClick={onResend}
                      >
                        {t("Didn't receive verification code? Resend")}
                      </button>
                    )}
                  </div>
                  <div className={styles.buttonGroup}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      block
                      className={styles.loginButton}
                      loading={verifyLoading}
                      disabled={otpDisabled}
                    >
                      {t("Verify")}
                    </Button>
                  </div>
                </form>
              )}

              {step === STEPS.CREATE_PIN && (
                <form
                  className={styles.stepForm}
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (createPinContinueDisabled) return;
                    onContinueCreatePin();
                  }}
                >
                  <h4 className={styles.title}>{t("Create PIN")}</h4>
                  <p className={styles.subtitle}>
                    {t("Please create your 4-digit login PIN")}
                  </p>
                  {topErrorAlert}
                  <p className={styles.otpStepLabel}>{t("Create PIN")}</p>
                  <div
                    className={`${styles.otpFieldWrap} ${styles.enterPinOtpFull}`}
                  >
                    <InputOtpField
                      length={4}
                      value={createPin}
                      onValueChange={setCreatePin}
                      mask
                      disabled={loading}
                    />
                  </div>
                  <div className={styles.buttonGroup}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      block
                      className={styles.loginButton}
                      disabled={createPinContinueDisabled}
                    >
                      {t("Continue")}
                    </Button>
                  </div>
                </form>
              )}

              {step === STEPS.CONFIRM_PIN && (
                <form
                  className={styles.stepForm}
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (confirmDisabled) return;
                    void onConfirmPinSubmit();
                  }}
                >
                  <h4 className={styles.title}>{t("Confirm your PIN")}</h4>
                  <p className={styles.subtitle}>
                    {t("Please re-enter to confirm your login PIN.")}
                  </p>
                  {topErrorAlert}
                  <p className={styles.otpStepLabel}>{t("Confirm PIN")}</p>
                  <div
                    className={`${styles.otpFieldWrap} ${styles.enterPinOtpFull}`}
                  >
                    <InputOtpField
                      length={4}
                      value={confirmPin}
                      onValueChange={(v) => {
                        setConfirmPin(v);
                        if (pinMismatch) setPinMismatch(false);
                      }}
                      mask
                      status={pinMismatch ? "error" : undefined}
                      disabled={loading}
                    />
                  </div>
                  {pinMismatch && (
                    <p className={styles.pinMismatchError} role="alert">
                      {t("PINs do not match. Please try again.")}
                    </p>
                  )}
                  <div className={styles.buttonGroup}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      block
                      className={styles.loginButton}
                      loading={setPinLoading}
                      disabled={confirmDisabled}
                    >
                      {t("Continue")}
                    </Button>
                  </div>
                </form>
              )}

              {step === STEPS.ENTER_PIN && (
                <form
                  className={styles.stepForm}
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (enterDisabled) return;
                    void onPinLogin();
                  }}
                >
                  <h4 className={styles.welcomeHeading}>
                    {welcomeBackName
                      ? `${t("Welcome back")}, ${welcomeBackName}`
                      : t("Welcome back")}
                  </h4>
                  <p className={styles.enterPinSubtitle}>
                    {t("Please enter your PIN to login")}
                  </p>
                  {topErrorAlert}
                  <div
                    className={`${styles.otpFieldWrap} ${styles.enterPinOtpFull}`}
                    data-testid="dev-login-pin"
                  >
                    <InputOtpField
                      length={4}
                      value={enterPin}
                      onValueChange={(v) => {
                        setEnterPin(v);
                        if (pinAuthError) setPinAuthError(false);
                      }}
                      mask
                      status={pinAuthError ? "error" : undefined}
                      disabled={loading}
                    />
                  </div>
                  <div className={styles.buttonGroup}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      block
                      className={styles.loginButton}
                      loading={pinLoginLoading}
                      disabled={enterDisabled}
                    >
                      {t("Login")}
                    </Button>
                  </div>
                  <div className={styles.enterPinForgotRow}>
                    <button
                      type="button"
                      className={styles.enterPinForgotLink}
                      disabled={loading}
                      onClick={() => {
                        setErrorMessage("");
                        startForgotPinFlow();
                      }}
                    >
                      {t("Forgot PIN?")}
                    </button>
                  </div>
                </form>
              )}

              <p className={styles.footer}>
                {t("Need help? Contact our")}{" "}
                <a href="mailto:support@propone.pk">{t("Customer Support")}</a>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Modal
        ref={impersonateModalRef}
        title={t("Impersonated User Email")}
        width={460}
        okText={t("Continue")}
        onOk={onImpersonateEmailOk}
        onCancel={() => impersonateModalRef.current?.setVisible(false)}
      >
        <Form form={impersonateForm} layout="vertical">
          <Form.Item
            name="email"
            validateTrigger={["onBlur", "onSubmit"]}
            rules={[
              { required: true, message: t("Please enter email") },
              { type: "email", message: t("Type a valid email address") },
            ]}
          >
            <Input
              placeholder={t("Enter Email")}
              size="large"
              onPressEnter={onImpersonateEmailOk}
            />
          </Form.Item>
        </Form>
      </Modal>
      <div className={styles.rightPanel} aria-hidden>
        <img
          src={`${process.env.PUBLIC_URL}/images/dev-login-illustration.png`}
          alt=""
          className={styles.rightPanelImage}
        />
      </div>
    </div>
  );
};

export default DeveloperLogin;
